import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useRenderCount,
  useMountTiming,
  perfTimestamp,
} from "../utils/perfLogger";
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@stockline/ui/components/table";
import { formatDecimal } from "../utils/decimalUtils";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useUnitsAndCurrency } from "@/hooks/useUnitsAndCurrency";
import { useUnits } from "@/query/hooks";
import { useLots } from "@/query/hooks/useLots";
import { useRecapColumnsOptional } from "../contexts/RecapContext";
import { useColumnResize } from "../hooks/useColumnResize";
import {
  useEffectivePriceLookup,
  useEffectivePriceWithSourceLookup,
} from "@/query/hooks/useGlobalPrices";
import { usePricingCache } from "../contexts/PricingCacheContext";
import { useStockCache } from "../contexts/StockCacheContext";
import { useFeatureFlag } from "@/query/hooks/useFeatureFlags";
import { useUnitMap } from "@/query/hooks/useUnitMap";
import { useRecapLocationsOffline } from "../hooks/useRecapLocationsOffline";
import { useDisabledColumnsConfig } from "../hooks/useDisabledColumnsConfig";
import { buildStockMapFromItems } from "@/query/hooks/useGlobalStocks";
import {
  ProductLookupsProvider,
  useProductLookups,
  type ProductLookupsContextValue,
} from "../contexts/ProductLookupsContext";
import {
  useRecapColumnWidthConfig,
  useRecapEmptyRowComponent,
  useRecapNewRowFocusColId,
  useRecapNoUnitLabel,
} from "../contexts/RecapPrimitivesContext";
import type { PreOrder, PreOrderFlow } from "@/app/api/types/preOrder";
import type { ColumnDef } from "../config/types";
import { DEFAULT_COLUMN_WIDTHS } from "../config/columns";
import type { UUID } from "crypto";
import { FlowRow } from "./cells/FlowRow";
import { ProductSelectCombo } from "./ui/ProductSelectCombo";
import { computeFlowStats } from "../services/calculationService";
import {
  resolveProductDefaults,
  type ProductDefaults,
} from "../utils/resolveProductDefaults";
import { useRecapDataContextOptional } from "../contexts/RecapContext";
import { useGlobalProducts } from "@/query/hooks/useUnifiedCache";
import type { ProductResponse } from "@/app/api/types/product";
import { isProposalPreOrder } from "../types/proposal.types";
import { useFlowSelectionOptional } from "../contexts/FlowSelectionContext";
import { SelectionCheckboxBox } from "./ui/SelectionCheckbox";
import { cn } from "@/lib/utils";

export interface ColumnResizeProps {
  getWidth: (colId: string) => number;
  resizingCols: { leftCol: string; rightCol: string } | null;
  handleResizeStart: (
    e: React.MouseEvent,
    leftCol: string,
    rightCol: string,
  ) => void;
}

/**
 * The product picker's corpus, derived once per FlowsTable instead of once per
 * `ProductSelectCombo` per open (STK-1863).
 *
 * Two predicates, both required for parity with the fetch this replaces
 * (`useProducts({ include_sleeping: false, includeAll: true })`):
 *  - non-variant: variants are reachable through their parent's `children`,
 *    so the dropdown lists parents only.
 *  - awake: on `/recap` the context products already went through
 *    `filterAndSearchProducts({ includeSleeping: false })`, but the
 *    outside-RecapProvider fallback below is a RAW `useGlobalProducts` and
 *    still carries sleeping rows. Filtering here keeps both surfaces
 *    identical to what the dropdown showed before the hoist.
 */
export function selectNonVariantProducts(
  products: ProductResponse[],
): ProductResponse[] {
  return products.filter(
    (p) => (!p.parents || p.parents.length === 0) && !p.is_sleeping,
  );
}

interface FlowsTableProps {
  preOrder: PreOrder;
  flows: PreOrderFlow[];
  disabled?: boolean;
  isLoading?: boolean;
  onFlowUpdate: (flowId: string, updates: Partial<PreOrderFlow>) => void;
  onFlowCreate: (defaults: ProductDefaults) => void;
  onFlowDelete: (flowId: string) => void;
  showPartnerColumn?: boolean;
  partnerId?: string | null;
  columnResize?: ColumnResizeProps;
  /** Override columns (for standalone use outside RecapProvider) */
  columns?: ColumnDef[];
  /**
   * Visual density. `compact` (default) preserves the historical tight
   * layout used inside partner cards / chat-panel proposal previews.
   * `spacious` is used by <PreOrderFocusView> where the table owns the
   * page width — uppercase semibold headers on a subtle background, taller
   * header row, more padding.
   */
  density?: "compact" | "spacious";
  /**
   * Render a totals row in `<tfoot>`. Each total appears under its
   * own column (qty totals by unit under "qté", weight total under
   * "poids", amount total under "total"). Used by the focus page's
   * table card.
   */
  showTotalsFooter?: boolean;
}

export function FlowsTable({
  preOrder,
  flows,
  disabled = false,
  isLoading = false,
  onFlowUpdate,
  onFlowCreate,
  onFlowDelete,
  showPartnerColumn = false,
  partnerId,
  columnResize,
  columns: columnsProp,
  density = "compact",
  showTotalsFooter = false,
}: FlowsTableProps) {
  useRenderCount(`FlowsTable[${preOrder.id.slice(0, 8)}]`);
  useMountTiming(`FlowsTable[${preOrder.id.slice(0, 8)}]`);
  // Stamp render-start so we can correlate against each FlowRow's
  // render-start timestamp below. If FlowRows trickle in well after this
  // mark, React is time-slicing inside the transition (header commits
  // first, rows after).
  perfTimestamp(`FlowsTable[${preOrder.id.slice(0, 8)}] render start`);
  const { t } = useTranslation();
  // Use prop columns when provided (standalone), otherwise read from RecapContext
  const contextColumns = useRecapColumnsOptional();
  const columns = columnsProp ?? contextColumns;
  const flowSelection = useFlowSelectionOptional();
  const allFlowIds = useMemo(() => flows.map((flow) => flow.id), [flows]);
  const setOrderedIds = flowSelection?.setOrderedIds;
  useEffect(() => {
    setOrderedIds?.(allFlowIds);
  }, [allFlowIds, setOrderedIds]);
  const selectedInTableCount = useMemo(() => {
    if (!flowSelection) return 0;
    return allFlowIds.filter(flowSelection.isSelected).length;
  }, [allFlowIds, flowSelection]);
  const allInTableSelected =
    flows.length > 0 && selectedInTableCount === flows.length;
  const someInTableSelected =
    selectedInTableCount > 0 && selectedInTableCount < flows.length;
  const handleHeaderSelection = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!flowSelection) return;
      if (allInTableSelected) flowSelection.clear();
      else flowSelection.selectAll(allFlowIds);
      if (event.detail > 0) event.currentTarget.blur();
    },
    [allFlowIds, allInTableSelected, flowSelection],
  );
  const { weightUnit, currencySymbol } = useUnitsAndCurrency();
  const { data: globalUnits = [] } = useUnits();
  // A sibling workspace (transport) can supply its own resize config (own
  // defaults + own storage key) via the primitives seam; null → recap defaults.
  const columnWidthConfig = useRecapColumnWidthConfig();
  // Flag-defined custom columns aren't in the static width maps, so seed their
  // default widths from each column's `width` — otherwise getWidth() returns
  // undefined and the column collapses.
  const customColumnWidths = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const col of columns) {
      if (col.customColumnKey) {
        acc[col.id] = Number.parseInt(col.width, 10) || 70;
      }
    }
    return acc;
  }, [columns]);
  const resizeConfig = useMemo(() => {
    const base = columnWidthConfig ?? { defaultWidths: DEFAULT_COLUMN_WIDTHS };
    return {
      ...base,
      defaultWidths: {
        ...(base.defaultWidths ?? DEFAULT_COLUMN_WIDTHS),
        ...customColumnWidths,
      },
    };
  }, [columnWidthConfig, customColumnWidths]);
  const ownResize = useColumnResize({
    enabled: !columnResize,
    ...resizeConfig,
  });
  const { getWidth, resizingCols, handleResizeStart } =
    columnResize ?? ownResize;

  // Per-pre-order lookups: mounted once here on expand, consumed via context
  // by every ProductCell (placeholder + editor) and EmptyProductRow. Keeps
  // cells essentially free (one useContext read) instead of each cell
  // re-subscribing to the same global caches.
  const showStocksRecap = useFeatureFlag("showStocksRecap") === true;
  const showPricesRecap = useFeatureFlag("showPricesRecap") !== false;
  // Editor-side flags hoisted up here so per-cell ProductCellEditor renders
  // don't re-invoke `useFeatureFlag` three times each. Flags are session-
  // stable, so reading them once at the table boundary is correct.
  const stockCustomFields = useFeatureFlag("stockCustomFields");
  const pickLocationEnabled = useFeatureFlag("pickLocation") === true;
  const isVivoMode = useFeatureFlag("export_mode") === "vivo";
  const { priceMaps } = usePricingCache();
  const getEffectivePrice = useEffectivePriceLookup(priceMaps);
  const getEffectivePriceWithSource =
    useEffectivePriceWithSourceLookup(priceMaps);

  // Phantom-price preview feeds the footer totals so they match what each
  // PriceCell shows (TARIF/LISTE pills). Committed price always wins.
  const headerPartnerId = preOrder.partner_id ?? null;
  const getPreviewPrice = useCallback(
    (f: PreOrderFlow) => {
      if (!f.product_id) return null;
      const partnerId = f.partner_id ?? headerPartnerId;
      return (
        getEffectivePriceWithSource(f.product_id, partnerId)?.value ?? null
      );
    },
    [getEffectivePriceWithSource, headerPartnerId],
  );

  const { stockMap: globalStockMap, stocks: rawStocks } = useStockCache();
  const unitMap = useUnitMap();
  // Lots and locations come from one subscription per pre-order, shared via
  // ProductLookupsContext. Previously every ProductCellEditor (one per flow
  // row) called `useLots()` and `useRecapLocationsOffline()` itself —
  // mounting a 6-flow PO arms 6 TanStack lot observers + 6 Dexie location
  // liveQueries. Lifting them here cuts each to a single subscription.
  const { data: lots = [] } = useLots();
  const { data: locations = [] } = useRecapLocationsOffline({
    enabled: pickLocationEnabled,
  });
  const recapDataForProducts = useRecapDataContextOptional();
  // Progressive-paint window: the central recap pipeline has the current
  // date scope's pre-orders but their flow rows are still streaming in.
  // When this PO has no flows YET (per-PO Dexie subscription in
  // PreOrderSection usually fills them within ~50 ms of mount), render a
  // delayed placeholder row instead of the "add product" row — the order
  // most likely HAS lines, we just don't know them yet. Outside the recap
  // provider (chat panel) the flag is absent and nothing changes.
  const flowsPending = recapDataForProducts?.flowsPending ?? false;
  const showFlowsPlaceholder = flowsPending && flows.length === 0 && !isLoading;
  // Outside RecapProvider (chat-page inline divider → PreOrderContent → here)
  // the recap data context isn't mounted, so the products map would be empty
  // and ProductCell couldn't resolve SKU/name. Fall back to the offline-first
  // global products hook — a thin Dexie liveQuery wrapper. Gated on
  // `enabled` so on /recap (where the recap context already provides
  // products) we don't pay the legacy-mapping memo or the TanStack mirror
  // useEffect — only the inner liveQuery subscriptions remain by React-hook
  // rules, and Dexie shares observers so the cost is constant.
  const globalProducts = useGlobalProducts({
    includeAll: true,
    enabled: recapDataForProducts == null,
  });
  const products = recapDataForProducts?.products ?? globalProducts.data;
  // Build a Map<id, product> once per pre-order (keyed on the products
  // reference). Includes children so cells can resolve variants too.
  const productMap = useMemo(() => {
    const m = new Map<string, ProductResponse>();
    for (const p of products) {
      m.set(p.id, p);
      if (p.children) {
        for (const c of p.children) m.set(c.id, c);
      }
    }
    return m;
  }, [products]);
  // One filter per FlowsTable, shared by every ProductSelectCombo inside it.
  const nonVariantProducts = useMemo(
    () => selectNonVariantProducts(products),
    [products],
  );
  // Per-PO Dexie flows (`useLiveFlowsForPreOrder`) are lean — `flow.product`
  // stays undefined and cells resolve it from ProductLookupsContext. The
  // calculation service can't: the compute-time weight override lives on
  // `flow.product.product_metadata`, so footer totals computed from lean
  // flows silently skipped it. Hydrate the product ref for totals only —
  // rows keep the original flow refs so FlowRow memoization is untouched.
  const flowsForTotals = useMemo(
    () =>
      flows.map((f) =>
        f.product || !f.product_id
          ? f
          : { ...f, product: productMap.get(f.product_id) ?? f.product },
      ),
    [flows, productMap],
  );
  // Unit-less quantities are labelled via the primitives seam: transport →
  // "colis", sales/purchases → "unité" (seam is null there, so fall back to
  // the localized default). When the unit column is disabled (transport, or
  // `showUnitRecap=false`) every quantity buckets under that one label.
  const noUnitLabel = useRecapNoUnitLabel() ?? t("ordersRecap.unite", "unité");
  const { unitDisabled } = useDisabledColumnsConfig();
  const totals = useMemo(
    () =>
      computeFlowStats(
        flowsForTotals,
        globalUnits,
        getPreviewPrice,
        noUnitLabel,
        unitDisabled,
      ),
    [flowsForTotals, globalUnits, getPreviewPrice, noUnitLabel, unitDisabled],
  );
  // Per-custom-column sums for the footer (keyed by column id). Each custom
  // value column sums its `flow_metadata[key]` across the order's flows.
  const customColumnTotals = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const col of columns) {
      if (!col.customColumnKey) continue;
      let sum = 0;
      for (const f of flowsForTotals) {
        const raw = (f.flow_metadata as Record<string, unknown> | null)?.[
          col.customColumnKey
        ];
        const n = typeof raw === "number" ? raw : Number(raw);
        if (Number.isFinite(n)) sum += n;
      }
      acc[col.id] = sum;
    }
    return acc;
  }, [columns, flowsForTotals]);
  const scopedStockMap = useMemo(() => {
    if (!preOrder.location_id) return globalStockMap;
    return buildStockMapFromItems(
      rawStocks.filter((s) => s.location_id === preOrder.location_id),
    );
  }, [preOrder.location_id, globalStockMap, rawStocks]);
  const lookups = useMemo<ProductLookupsContextValue>(
    () => ({
      showStocks: showStocksRecap,
      showPrices: showPricesRecap,
      stockMap: scopedStockMap,
      getEffectivePrice,
      getEffectivePriceWithSource,
      unitMap,
      productMap,
      products,
      nonVariantProducts,
      lots,
      locations,
      stockCustomFields,
      pickLocationEnabled,
      isVivoMode,
    }),
    [
      showStocksRecap,
      showPricesRecap,
      scopedStockMap,
      getEffectivePrice,
      getEffectivePriceWithSource,
      unitMap,
      productMap,
      products,
      nonVariantProducts,
      lots,
      locations,
      stockCustomFields,
      pickLocationEnabled,
      isVivoMode,
    ],
  );

  // Total of all column widths — used to convert px → % for proportional rendering
  const totalWidth = useMemo(
    () => columns.reduce((sum, col) => sum + getWidth(col.id), 0),
    [columns, getWidth],
  );
  const getColPercent = useCallback(
    (colId: string) => `${(getWidth(colId) / totalWidth) * 100}%`,
    [getWidth, totalWidth],
  );

  // Auto-focus the most useful cell when a new flow is added (optimistic
  // create). The target column is supplied by the primitives seam
  // (`newRowFocusColId`, default "quantity"): sales/purchases commit the
  // product in the add-row, so the next edit is quantity; transport commits
  // the sender, so the next edit is the recipient. We prefer the cell's
  // `[data-tab-cell]` marker (recap cells), falling back to any input/button
  // for picker subcomponents that live outside `recap/components/cells`.
  const newRowFocusColId = useRecapNewRowFocusColId();
  const EmptyRowOverride = useRecapEmptyRowComponent();
  const tableRef = useRef<HTMLDivElement>(null);
  const prevFlowCountRef = useRef(flows.length);
  useLayoutEffect(() => {
    const prevCount = prevFlowCountRef.current;
    prevFlowCountRef.current = flows.length;
    if (flows.length !== prevCount + 1) return;

    const newFlow = flows[flows.length - 1];
    const table = tableRef.current;
    if (!newFlow || !table) return;

    const newRow = table.querySelector<HTMLElement>(
      `[data-flow-id="${newFlow.id}"]`,
    );
    if (!newRow) return;

    const td = newRow.querySelector<HTMLElement>(
      `[data-col-id="${newRowFocusColId}"]`,
    );
    const tabCell = td?.querySelector<HTMLElement>("[data-tab-cell]");
    if (tabCell) {
      tabCell.focus();
      return;
    }
    const fallback = td?.querySelector<HTMLElement>("input, button");
    fallback?.focus();
  }, [flows, newRowFocusColId]);

  // Geometry for the guide's selection-column anchor (`flow-select-column`):
  // the real rendered rect of the checkbox column spanning ONLY the data rows
  // (first leg top → last leg bottom), not the header or the trailing add-row.
  // Read the DOM — the column id differs per surface ("select" vs
  // "transport_select") and the fixed-layout table stretches percentages.
  const [selectColBand, setSelectColBand] = useState<{
    top: number;
    left: number;
    height: number;
    width: number;
  } | null>(null);
  useLayoutEffect(() => {
    const wrap = tableRef.current;
    if (!wrap) return;
    const measure = () => {
      const rows = wrap.querySelectorAll<HTMLElement>(
        '[data-testid="flow-row"]',
      );
      // The select column is not guaranteed to be the leftmost — measure its
      // ACTUAL header cell (via the select-all control) so the band lands on the
      // checkboxes, not column 0. Fall back to the first header cell.
      const selectHead =
        wrap
          .querySelector<HTMLElement>('[data-testid="flow-table-select-all"]')
          ?.closest("th") ?? wrap.querySelector<HTMLElement>("thead th");
      if (rows.length === 0 || !selectHead) {
        setSelectColBand(null);
        return;
      }
      const wrapBox = wrap.getBoundingClientRect();
      const headBox = selectHead.getBoundingClientRect();
      const first = rows[0].getBoundingClientRect();
      const last = rows[rows.length - 1].getBoundingClientRect();
      setSelectColBand({
        top: first.top - wrapBox.top,
        // + scrollLeft: convert the viewport offset to the scroll container's
        // content coordinate (the band is absolutely positioned inside it).
        left: headBox.left - wrapBox.left + wrap.scrollLeft,
        height: last.bottom - first.top,
        width: headBox.width,
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [flows.length]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <ProductLookupsProvider value={lookups}>
      <div
        ref={tableRef}
        data-testid="recap-flows-table"
        className="relative overflow-x-auto"
        data-recap-table
      >
        {/* Guide marker: present once at least two legs are selected — the
            batch-edit guide's "select several" step advances on it (not on the
            first selection, when the island merely appears). It's an empty
            `aria-hidden` span (zero-size, invisible to sighted + AT users), but
            it must NOT carry `hidden`/`display:none`: the guide's appear-target
            resolver (`isVisibleTarget`) rejects display-none elements, so a
            hidden marker would never resolve and the step would time out. */}
        {selectedInTableCount >= 2 && (
          <span data-testid="flow-batch-2plus" aria-hidden="true" />
        )}
        {/* Guide geometry anchor: an invisible band over the whole selection
            (checkbox) column, so the batch-edit guide spotlights WHERE to tick
            across every row rather than one row or the entire table. A table
            column is not a single DOM node, so this absolute strip stands in
            for it (measured from the select column's header cell + the data-row
            span). pointer-events-none so it never blocks a click. */}
        {flowSelection && selectColBand && (
          <div
            data-testid="flow-select-column"
            aria-hidden="true"
            className="pointer-events-none absolute"
            style={{
              top: `${selectColBand.top}px`,
              left: `${selectColBand.left}px`,
              height: `${selectColBand.height}px`,
              width: `${selectColBand.width}px`,
            }}
          />
        )}
        <table
          className="caption-bottom text-sm"
          style={{
            tableLayout: "fixed",
            width: "100%",
          }}
        >
          <TableHeader>
            <TableRow
              // Stable handle for the guidance overlay's "read the column
              // headers" step (the row itself, not any one column).
              data-testid="recap-flows-header"
              className={cn(
                "group",
                density === "spacious" ? "h-9 bg-gray-50/70" : "h-6",
              )}
            >
              {columns.map((col, colIdx) => {
                const nextCol = columns[colIdx + 1];
                // Show handle only between two resizable columns
                const showHandle =
                  col.resizable !== false &&
                  nextCol != null &&
                  nextCol.resizable !== false;
                const isResizing =
                  resizingCols?.leftCol === col.id &&
                  resizingCols?.rightCol === nextCol?.id;
                const headerClass =
                  density === "spacious"
                    ? "text-[11px] font-semibold uppercase tracking-wide text-gray-700 relative h-auto py-2 px-2 border-b border-gray-300"
                    : "text-[10px] font-medium text-gray-400 relative h-auto py-0.5 px-1";
                const isSelectionHeader = col.id === "select" && flowSelection;
                return (
                  <TableHead
                    key={col.id}
                    style={{
                      width: getColPercent(col.id),
                      minWidth: col.minWidth,
                    }}
                    className={cn(
                      headerClass,
                      col.align === "right" && "text-right",
                      isSelectionHeader && "px-0 text-center",
                    )}
                  >
                    {isSelectionHeader ? (
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={
                          someInTableSelected ? "mixed" : allInTableSelected
                        }
                        aria-label={t(
                          "ordersRecap.flowBatch.selectAll",
                          "Select all",
                        )}
                        data-testid="flow-table-select-all"
                        disabled={disabled || flows.length === 0}
                        onClick={handleHeaderSelection}
                        className={cn(
                          "group/checkbox mx-auto flex h-7 w-full items-center justify-center rounded-md outline-none transition-opacity duration-150",
                          "disabled:cursor-not-allowed disabled:opacity-50",
                          !flowSelection.hasSelection &&
                            "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                        )}
                      >
                        <SelectionCheckboxBox
                          checked={allInTableSelected}
                          indeterminate={someInTableSelected}
                          disabled={disabled}
                        />
                      </button>
                    ) : (
                      (col.headerLabel ??
                      (col.headerKey ? t(col.headerKey) : ""))
                    )}
                    {showHandle && (
                      <div
                        className="absolute right-0 top-1 bottom-1 -mr-1 w-2 cursor-col-resize z-10 flex items-center justify-center group/separator"
                        onMouseDown={(e) =>
                          handleResizeStart(e, col.id, nextCol.id)
                        }
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className={`w-px h-full transition-all ${
                            isResizing
                              ? "bg-blue-400 w-0.5"
                              : "bg-gray-200 group-hover/separator:bg-gray-400 group-hover/separator:w-0.5"
                          }`}
                        />
                      </div>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {flows.map((flow) => (
              <FlowRow
                key={flow.id}
                flow={flow}
                preOrder={preOrder}
                columns={columns}
                availableUnits={globalUnits}
                disabled={disabled}
                showPartnerColumn={showPartnerColumn}
                onFlowUpdate={onFlowUpdate}
                onFlowDelete={onFlowDelete}
                getWidth={getWidth}
              />
            ))}
            {/* Flow rows still streaming in for this PO (progressive paint).
                The row is invisible for its first 150 ms (delayed fade-in) so
                the common fast path — per-PO Dexie resolving within ~50 ms —
                never flashes it. */}
            {showFlowsPlaceholder && (
              <TableRow
                data-testid="flows-pending-placeholder"
                className={`opacity-0 animate-recap-delayed-fade-in ${
                  density === "spacious" ? "h-9" : "h-6"
                }`}
              >
                <TableCell colSpan={columns.length} className="px-2 py-1">
                  <div className="h-2.5 w-1/3 min-w-24 rounded bg-gray-100 animate-pulse" />
                </TableCell>
              </TableRow>
            )}
            {/* Empty row for adding a new flow (hidden in per-product view).
                Kept mounted while the table is disabled (e.g. during a
                Validate/Save flush) — just made non-interactive — so it doesn't
                disappear and reappear mid-save. Swapped out for the pending
                placeholder above while this PO's flows are still streaming —
                an "add a product" affordance on an order whose lines simply
                haven't arrived yet reads as data loss. A sibling workspace can
                swap the product-picker add-row for its own (transport injects a
                sender picker) via the `emptyRowComponent` primitives seam;
                default keeps EmptyProductRow. */}
            {!isLoading &&
              !showPartnerColumn &&
              !showFlowsPlaceholder &&
              (EmptyRowOverride ? (
                <EmptyRowOverride
                  columns={columns}
                  getWidth={getWidth}
                  onFlowCreate={onFlowCreate}
                  preOrder={preOrder}
                  partnerId={partnerId}
                  disabled={disabled}
                />
              ) : (
                <EmptyProductRow
                  columns={columns}
                  getWidth={getWidth}
                  onFlowCreate={onFlowCreate}
                  preOrder={preOrder}
                  partnerId={partnerId}
                  disabled={disabled}
                />
              ))}
          </TableBody>
          {showTotalsFooter && flows.length > 0 && (
            <TotalsFooterRow
              columns={columns}
              getColPercent={getColPercent}
              totals={totals}
              customColumnTotals={customColumnTotals}
              weightUnit={weightUnit}
              currencySymbol={currencySymbol}
              t={t}
            />
          )}
        </table>
      </div>
    </ProductLookupsProvider>
  );
}

/** Inline empty row with product selector — auto-creates flow on product pick. */
function EmptyProductRow({
  columns,
  getWidth,
  onFlowCreate,
  preOrder,
  partnerId,
  disabled = false,
}: {
  columns: import("../config/types").ColumnDef[];
  getWidth: (id: string) => number;
  onFlowCreate: (defaults: ProductDefaults) => void;
  preOrder: PreOrder;
  partnerId?: string | null;
  /** Keep the row mounted but non-interactive (e.g. during a Validate flush). */
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  // Price/stock/showFlags/unitMap/products come from the per-pre-order
  // context that FlowsTable provides — no duplicate subscriptions to global
  // caches. `products` is resolved by the parent (recap context on /recap,
  // offline-first global on /chat), so EmptyProductRow doesn't need to
  // re-do the fallback chain.
  // All flags, lots, locations, and stock subscriptions flow through the
  // single context populated by the parent FlowsTable — no per-row hook calls.
  const lookups = useProductLookups();
  const {
    showStocks,
    showPrices,
    stockMap,
    getEffectivePrice,
    products,
    locations,
    stockCustomFields,
    pickLocationEnabled,
  } = lookups ?? {
    showStocks: false,
    showPrices: false,
    stockMap: new Map(),
    getEffectivePrice: () => null,
    getEffectivePriceWithSource: () => null,
    products: [] as ProductResponse[],
    locations: [],
    stockCustomFields: undefined,
    pickLocationEnabled: false,
    isVivoMode: false,
  };
  const { stocks: rawStocks } = useStockCache();

  const stockLocationLabel = useMemo(() => {
    if (!preOrder.location_id) return null;
    const loc = locations.find((l) => l.id === preOrder.location_id);
    if (!loc) return null;
    return loc.code ? `${loc.code} - ${loc.name}` : loc.name;
  }, [preOrder.location_id, locations]);

  const stockLookupFn = useCallback(
    (productId: string) => stockMap.get(productId) ?? null,
    [stockMap],
  );

  const effectivePartnerId = partnerId ?? preOrder.partner_id ?? null;

  const priceLookupFn = useCallback(
    (productId: string) => getEffectivePrice(productId, effectivePartnerId),
    [getEffectivePrice, effectivePartnerId],
  );

  const handleProductChange = useCallback(
    (
      productId: UUID | null,
      subproductId?: UUID | null,
      product?: ProductResponse | null,
    ) => {
      if (disabled || !productId) return;
      const defaults = resolveProductDefaults({
        productId: subproductId ?? productId,
        product: product ?? null,
        products,
        getEffectivePrice,
        partnerId: effectivePartnerId,
        // Price preview lives in the PriceCell pill now (TARIF / LISTE)
        // — picking a product never commits a price into the flow.
        skipPricePrefill: true,
      });
      onFlowCreate(defaults);
    },
    [disabled, onFlowCreate, products, getEffectivePrice, effectivePartnerId],
  );

  // The product picker must land in the PRODUCT column — NOT `columns[0]`,
  // which is now the leading `select` checkbox column (added by the flow
  // batch-selection feature). Render an empty cell for every column before
  // the product, the picker in the product column, then colSpan the rest.
  // If a column set omits `product` entirely, fall back to the first column
  // wide enough to host the picker (skipping the narrow non-resizable
  // `select` checkbox) so it never squeezes back into the 14px column.
  const foundProductIndex = columns.findIndex((c) => c.id === "product");
  const fallbackIndex = Math.max(
    0,
    columns.findIndex((c) => c.id !== "select"),
  );
  const productIndex =
    foundProductIndex >= 0 ? foundProductIndex : fallbackIndex;
  const leadingCols = columns.slice(0, productIndex);
  const productCol = columns[productIndex] ?? columns[0];
  const trailingCols = columns.length - productIndex - 1;
  const isProposalContext = isProposalPreOrder(preOrder);
  const baseRowClass = isProposalContext
    ? "group/row hover:bg-violet-100/60 bg-violet-50/50"
    : "group/row hover:bg-gray-100 bg-gray-50";
  // Disabled (e.g. mid-Validate): stay mounted but non-interactive + dimmed.
  const rowClass = disabled
    ? `${baseRowClass} opacity-50 pointer-events-none`
    : baseRowClass;

  return (
    <TableRow
      className={rowClass}
      data-new-product-row
      data-testid="flow-empty-add-row"
      data-attr="flow-empty-add-row"
    >
      {leadingCols.map((col) => (
        <td
          key={col.id}
          className="px-1 py-1"
          style={{ width: `${getWidth(col.id)}px` }}
        />
      ))}
      <td
        className="px-1 py-1"
        style={{
          width: `${getWidth(productCol?.id ?? "product")}px`,
        }}
      >
        {/* Min-height floor so the add-row matches data-row height (h-7)
            regardless of whether the price/stock pills render. Data rows get
            their height from the h-7 numeric cells; this row has only the
            product cell, whose closed display would otherwise collapse to its
            ~20px text height when stocks are off (showStocksRecap defaults
            false) — making the add-row look tiny on those tenants. */}
        <div className="flex items-center min-h-[1.75rem]">
          <ProductSelectCombo
            value={null}
            product={null}
            isLastRow
            disabled={disabled}
            onChange={handleProductChange}
            placeholder={t("ordersRecap.selectProductToAdd")}
            // Same shared catalog the data rows' ProductCell uses (STK-1863).
            allProductsOverride={lookups?.nonVariantProducts}
            stockLookup={showStocks ? stockLookupFn : undefined}
            priceLookup={showPrices ? priceLookupFn : undefined}
            hideClosedPricePill
            stockCustomFields={stockCustomFields}
            stockLocationLabel={stockLocationLabel}
            stockLocations={
              pickLocationEnabled
                ? locations.map((l) => ({
                    id: l.id,
                    name: l.name,
                    code: l.code,
                  }))
                : undefined
            }
            stockLocationId={preOrder.location_id ?? null}
            rawStocks={
              showStocks && pickLocationEnabled ? rawStocks : undefined
            }
          />
        </div>
      </td>
      {trailingCols > 0 && <td colSpan={trailingCols} />}
    </TableRow>
  );
}

interface TotalsFooterRowProps {
  columns: ColumnDef[];
  getColPercent: (colId: string) => string;
  totals: ReturnType<typeof computeFlowStats>;
  /** Per-custom-column sums, keyed by column id. */
  customColumnTotals: Record<string, number>;
  weightUnit: string;
  currencySymbol: string;
  t: ReturnType<typeof useTranslation>["t"];
}

/**
 * Per-column totals row rendered in `<tfoot>`. Each total lands under
 * its own column so the user can scan vertically — qty totals by unit
 * under the quantity column, weight total under weight, amount total
 * under total. Other columns get an empty cell to preserve the grid.
 *
 * Mounted only on the focus-page table card (via `showTotalsFooter`).
 * Compact tables (chat-panel preview, partner cards) keep the existing
 * inline totals on the parent header.
 */
function TotalsFooterRow({
  columns,
  getColPercent,
  totals,
  customColumnTotals,
  weightUnit,
  currencySymbol,
  t,
}: TotalsFooterRowProps) {
  const cellByCol: Record<string, React.ReactNode> = {};

  // Custom value columns: sum under each column, mirroring the qty/weight
  // totals. Zero sums stay blank so an unused column doesn't read as "0".
  for (const col of columns) {
    if (!col.customColumnKey) continue;
    const sum = customColumnTotals[col.id] ?? 0;
    if (sum <= 0) continue;
    cellByCol[col.id] = (
      <span className="font-semibold text-gray-900 tabular-nums">
        {formatDecimal(sum)}
      </span>
    );
  }

  // Quantity column: stack each unit's total on its own line so mixed
  // (e.g. COL + KG) orders show both without truncation. Keep zero-qty
  // units in the list — a flow with a unit set but no quantity yet still
  // counts as "this unit appears in the order".
  const unitEntries = Object.entries(totals.quantitiesByUnit);
  if (unitEntries.length > 0) {
    cellByCol.quantity = (
      <div className="flex flex-col items-start gap-0.5">
        {unitEntries.map(([unit, qty]) => (
          <span
            key={unit}
            className="font-semibold text-gray-900 tabular-nums leading-tight"
          >
            {formatDecimal(qty)}{" "}
            <span className="font-normal text-gray-500 text-xs">{unit}</span>
          </span>
        ))}
      </div>
    );
  }

  if (totals.totalWeight > 0) {
    cellByCol.weight = (
      <span className="font-semibold text-gray-900 tabular-nums">
        {formatDecimal(totals.totalWeight)}{" "}
        <span className="font-normal text-gray-500 text-xs">{weightUnit}</span>
      </span>
    );
  }

  if (totals.totalAmount > 0) {
    cellByCol.total = (
      <span
        className="font-semibold text-gray-900 tabular-nums"
        data-testid="focus-table-total"
      >
        {totals.totalAmount.toFixed(2)}{" "}
        <span className="font-normal text-gray-500 text-xs">
          {currencySymbol}
        </span>
      </span>
    );
  }

  // First non-blank column gets the "Sous-total" label so the row reads
  // left-to-right. Skip blank-only columns (image / status / actions).
  cellByCol.product = (
    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
      {t("ordersRecap.subTotal", { defaultValue: "Sous-total" })}
    </span>
  );

  return (
    <TableFooter className="bg-gray-50/60">
      <TableRow className="border-t border-gray-300 hover:bg-transparent">
        {columns.map((col) => {
          const content = cellByCol[col.id] ?? null;
          return (
            <TableCell
              key={col.id}
              style={{ width: getColPercent(col.id) }}
              className={`text-sm py-3 px-2 align-top ${
                col.align === "right" ? "text-right" : ""
              }`}
            >
              {content}
            </TableCell>
          );
        })}
      </TableRow>
    </TableFooter>
  );
}
